import {
  Modal,
  NumberField,
  useForm,
  PrimaryButton,
  SecondaryButton,
} from '@weave/design-system';
import { useChangeTagPercentageMutation } from 'apis/wetags/mutations';
import { getTagQuery, getTagStatsQuery } from 'apis/wetags/queriesV2';
import { useEffect } from 'react';

export function ChangeTagPercentageModal(props: {
  isOpen: boolean;
  onClose: () => void;
  namespaceId: string;
  tagId: string;
  currentPercentage?: number;
}) {
  const { isOpen, onClose, namespaceId, tagId, currentPercentage } = props;
  const { mutateAsync: changePercentageAsync, isLoading: isChangingPercentage } =
    useChangeTagPercentageMutation();

  // Form for changing percentage
  const { formProps, getFieldProps, isComplete, seedValues } = useForm({
    fields: {
      percentage: {
        type: 'number',
        validator: ({ value }) => {
          const num = parseFloat(value);
          return !isNaN(num) && num >= 0 && num <= 100
            ? ''
            : 'Percentage must be between 0 and 100';
        },
      },
    },
    onSubmit: async (values) => {
      if (!values.percentage) return;
      const newPercentage = parseFloat(values.percentage);

      await changePercentageAsync({
        namespaceId,
        tagId,
        percentage: newPercentage,
      });

      // Invalidate queries to refresh data
      getTagQuery.invalidate({ namespaceId, tagId });
      getTagStatsQuery.invalidate({ namespaceId, tagId });

      onClose();
    },
  });

  // this sets the form values correctly when the modal opens initially and again after changing the percentage
  // setting the value in useForm does not update it on subsequent opens
  useEffect(() => {
    seedValues({ percentage: String(currentPercentage ?? 0) });
  }, [currentPercentage]);

  return (
    <Modal show={isOpen} onClose={onClose}>
      <Modal.Header>Change Tag Percentage</Modal.Header>
      <Modal.Body>
        <p>Set the percentage of entities that will be implicitly tagged.</p>
        <form {...formProps}>
          <NumberField
            {...getFieldProps('percentage')}
            label="Percentage"
            min={0}
            max={100}
            disabled={isChangingPercentage}
          />
          <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem' }}>
            <PrimaryButton type="submit" disabled={!isComplete || isChangingPercentage}>
              Save
            </PrimaryButton>
            <SecondaryButton
              type="button"
              onClick={onClose}
              disabled={isChangingPercentage}
            >
              Cancel
            </SecondaryButton>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
